import { roundToDecimals, sumAmounts } from "../../../../lib/money";

/**
 * Sums the Selling Price line items (Selling Price, Accessories) into the
 * Total Selling Price subtotal.
 * @param {{ amount: number }[]} items - The Selling Price line items.
 * @returns {number} The Total Selling Price.
 */
export function calculateTotalSellingPrice(items: Array<{ amount: number }>): number {
  return sumAmounts(items);
}

/**
 * Sums the Down Payment line items (Cash Down, Gross Trade-In, Trade-in
 * Payoff) and flips the sign, since Total Down Payment deducts from the
 * Itemized Amount Financed rather than adding to it. Trade-in Payoff is
 * already a negative amount within `items` (it subtracts from the group),
 * the same "sign lives on the item" convention used elsewhere.
 * @param {{ amount: number }[]} items - The Down Payment line items.
 * @returns {number} The Total Down Payment deduction (negative).
 */
export function calculateTotalDownPaymentDeduction(items: Array<{ amount: number }>): number {
  // `|| 0` normalizes -0 (e.g. an empty or all-zero `items`) to +0, since
  // `-0 === 0` is true but `Object.is(-0, 0)` is false — the sign-flip below
  // would otherwise produce a -0 that trips strict-equality checks.
  return -sumAmounts(items) || 0;
}

/**
 * Rolls up the Total Selling Price, the Total Down Payment deduction, and
 * the remaining fee/product line items (Sales Tax, VSC, GAP, Other Backend
 * Products, Total Customer Fees) into the Itemized Amount Financed.
 * @param {number} totalSellingPrice - See calculateTotalSellingPrice.
 * @param {number} totalDownPaymentDeduction - See calculateTotalDownPaymentDeduction.
 * @param {{ amount: number }[]} feeItems - The remaining fee/product line items.
 * @returns {number} The Itemized Amount Financed.
 */
export function calculateItemizedAmountFinanced(
  totalSellingPrice: number,
  totalDownPaymentDeduction: number,
  feeItems: Array<{ amount: number }>
): number {
  return roundToDecimals(totalSellingPrice + totalDownPaymentDeduction + sumAmounts(feeItems));
}
